/**
 * Origin subscription flow.
 * Completely separate from the main-questionnaire inline script — same general
 * interaction patterns (step machine, auto-advance, sessionStorage persistence)
 * re-implemented here as a standalone module.
 *
 * NOTE: The vendor/roastery blurb (see SubscriptionScoring below) is still a
 * simulation. Cart + price always use the dedicated subscription product
 * (quantity + frequency as variants, remaining answers as line-item properties).
 */
(function () {
  var SUBSCRIPTION_RESULTS_STATE_KEY = 'subscriptionResultsState';
  var SUBSCRIPTION_TOTAL_STEPS = 4;
  var SUBSCRIPTION_AUTO_ADVANCE_MS = 300;
  // Billing is monthly and open-ended; the customer no longer picks a term. The
  // term is pinned to the 12-month baseline, which is the monthly price already
  // shown on the Step 3 quantity cards and what the backend keeps as its
  // "minimum commitment, display only" term_months.
  var SUBSCRIPTION_BASELINE_TERM_MONTHS = 12;
  var SUBSCRIPTION_FIELD_TO_SHEET_ID = {
    quantity: 'subscriptionEditQuantity',
    deliveryDay: 'subscriptionEditDeliveryDay',
    profile: 'subscriptionEditProfile',
    grind: 'subscriptionEditGrind',
  };

  var SUBSCRIPTION_DELIVERY_DAY_LABELS = {
    10: 'כל 10 לחודש',
    20: 'כל 20 לחודש',
  };
  var SUBSCRIPTION_PROFILE_LABELS = {
    italian: 'סגנון איטלקי ועשיר',
    classic: 'סגנון קלאסי "בית קפה"',
    modern: 'סגנון מודרני',
  };
  var SUBSCRIPTION_PROFILE_SHORT_DESC = {
    italian: 'עשיר ושוקולדי',
    classic: 'עדין ומתוק',
    modern: 'פירותי וקליל',
  };
  var SUBSCRIPTION_GRIND_LABELS = {
    whole: 'פולים שלמים',
    espresso: 'טחינה לאספרסו - דקה',
    moka: 'טחינה למקינטה / קפה שחור - דקה-בינונית',
    v60: 'טחינה לפילטר / V60 - בינונית',
    frenchpress: "טחינה לפרנץ' פרס / Cold Brew - גסה",
  };
  var SUBSCRIPTION_GRIND_SHORT_LABELS = {
    whole: 'פולים שלמים',
    espresso: 'אספרסו - טחינה',
    moka: 'מקינטה - טחינה',
    v60: 'פילטר / V60 - טחינה',
    frenchpress: "פרנץ' פרס - טחינה",
  };
  var SUBSCRIPTION_PROFILE_TILE_LABELS = {
    italian: 'האיטלקי: עשיר',
    classic: 'הקלאסי: מאוזן',
    modern: 'המודרני: פירותי',
  };

  /* PLACEHOLDER: content-based mapping from this flow's answers to the existing
     coffee_tastes / coffee_prep_methods metafield tags, used only to simulate a
     vendor recommendation. Replace when the real subscription logic exists. */
  var SUBSCRIPTION_PROFILE_TASTE_TAG = {
    italian: 'Rich & Chocolaty',
    classic: 'Sweet & Caramelly',
    modern: 'Fruity & Floral',
  };
  var SUBSCRIPTION_GRIND_PREP_TAG = {
    whole: null,
    espresso: 'Espresso',
    moka: 'MokaPot',
    v60: 'Filter',
    frenchpress: null,
  };
  var SUBSCRIPTION_FALLBACK_VENDORS = ['אור זך', 'קנופי', 'קפה איכות', 'הקליה המקומית'];
  var SUBSCRIPTION_FIELD_TO_STEP = {
    profile: 'Taste',
    grind: 'Grind',
    quantity: 'Amount',
    deliveryDay: 'Date',
  };

  window.subscriptionAnswers = window.subscriptionAnswers || {
    quantity: null,
    // Pinned to the baseline term — no longer customer-selectable. Kept so the
    // variant lookup, price math, and backend payload work unchanged.
    frequency: SUBSCRIPTION_BASELINE_TERM_MONTHS,
    deliveryDay: null,
    profile: null,
    grind: null,
  };

  /* ── Simulated recommendation engine ──────────────────────────────────── */
  window.SubscriptionScoring = (function () {
    var cachedProducts = null;

    function loadProducts() {
      if (cachedProducts) return cachedProducts;
      var el = document.getElementById('subscription-products-data');
      if (!el) {
        cachedProducts = [];
        return cachedProducts;
      }
      try {
        cachedProducts = JSON.parse(el.textContent || '[]') || [];
      } catch (e) {
        cachedProducts = [];
      }
      return cachedProducts;
    }

    function simpleHash(str) {
      var hash = 0;
      for (var i = 0; i < str.length; i++) {
        hash = (hash * 31 + str.charCodeAt(i)) | 0;
      }
      return Math.abs(hash);
    }

    function fallbackVendor(profile, grind) {
      var idx = simpleHash(String(profile) + '|' + String(grind)) % SUBSCRIPTION_FALLBACK_VENDORS.length;
      return { vendorShortName: SUBSCRIPTION_FALLBACK_VENDORS[idx], vendorName: SUBSCRIPTION_FALLBACK_VENDORS[idx], product: null };
    }

    function pickVendor(profile, grind) {
      var products = loadProducts();
      var tasteTag = SUBSCRIPTION_PROFILE_TASTE_TAG[profile];
      var prepTag = SUBSCRIPTION_GRIND_PREP_TAG[grind];

      var scored = products
        .map(function (product) {
          var tastes = (product.metafields && product.metafields.coffee_tastes) || [];
          var preps = (product.metafields && product.metafields.coffee_prep_methods) || [];
          var score = 0;
          if (tasteTag && tastes.indexOf(tasteTag) !== -1) score += 2;
          if (prepTag && preps.indexOf(prepTag) !== -1) score += 1;
          return { product: product, score: score };
        })
        .filter(function (entry) {
          return entry.score > 0 && entry.product.available;
        });

      if (!scored.length) {
        return fallbackVendor(profile, grind);
      }

      var byVendor = {};
      scored.forEach(function (entry) {
        var key = entry.product.vendor_short_name || entry.product.vendor || 'unknown';
        if (!byVendor[key]) byVendor[key] = { total: 0, best: entry };
        byVendor[key].total += entry.score;
        if (entry.score > byVendor[key].best.score) byVendor[key].best = entry;
      });

      var bestVendorKey = Object.keys(byVendor).sort(function (a, b) {
        return byVendor[b].total - byVendor[a].total;
      })[0];

      var winner = byVendor[bestVendorKey];
      return {
        vendorShortName: bestVendorKey,
        vendorName: winner.best.product.vendor || bestVendorKey,
        product: winner.best.product,
      };
    }

    return { pickVendor: pickVendor, loadProducts: loadProducts };
  })();

  /* ── Subscription product (quantity + frequency variants) ─────────────── */
  var SUBSCRIPTION_QUANTITY_TO_KG = { 4: 1, 8: 2, 16: 4 };

  function loadSubscriptionProduct() {
    if (window.subscriptionProduct && window.subscriptionProduct.variants) {
      return window.subscriptionProduct;
    }
    var el = document.getElementById('subscription-product-data');
    if (!el) {
      window.subscriptionProduct = { variants: [] };
      return window.subscriptionProduct;
    }
    try {
      window.subscriptionProduct = JSON.parse(el.textContent || '{}') || { variants: [] };
    } catch (e) {
      window.subscriptionProduct = { variants: [] };
    }
    if (!window.subscriptionProduct.variants) window.subscriptionProduct.variants = [];
    return window.subscriptionProduct;
  }

  function subscriptionOptionNumber(value) {
    var match = String(value || '').match(/(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  }

  function findSubscriptionVariant(quantity, frequency) {
    var kg = SUBSCRIPTION_QUANTITY_TO_KG[quantity];
    var months = parseInt(frequency, 10);
    if (!kg || !months) return null;
    var variants = loadSubscriptionProduct().variants || [];
    for (var i = 0; i < variants.length; i++) {
      var variant = variants[i];
      if (
        subscriptionOptionNumber(variant.option1) === kg &&
        subscriptionOptionNumber(variant.option2) === months
      ) {
        return variant;
      }
    }
    return null;
  }

  /*
   * Monthly prices from OSS (GET /api/public/plans), keyed by the variant's
   * trailing numeric id. Empty until the fetch succeeds; every price lookup
   * falls back to the variant price / term when a plan is missing.
   */
  var subscriptionPlanMonthlyAgorot = {};

  function subscriptionNumericId(id) {
    var match = String(id == null ? '' : id).match(/(\d+)\s*$/);
    return match ? match[1] : null;
  }

  // Monthly price in shekels for a bag count at the pinned term, or null.
  function subscriptionMonthlyPrice(quantity, frequency) {
    var variant = findSubscriptionVariant(quantity, frequency);
    if (!variant) return null;
    var planAgorot = subscriptionPlanMonthlyAgorot[subscriptionNumericId(variant.id)];
    if (typeof planAgorot === 'number') return planAgorot / 100;
    if (typeof variant.price !== 'number') return null;
    return variant.price / 100 / (parseInt(frequency, 10) || 1);
  }

  // Overwrite the Liquid-rendered prices on the Step 3 quantity cards.
  function renderSubscriptionQuantityCardPrices() {
    document.querySelectorAll('[data-subscription-monthly-price]').forEach(function (el) {
      var quantity = parseInt(el.getAttribute('data-subscription-monthly-price'), 10);
      var monthly = subscriptionMonthlyPrice(quantity, SUBSCRIPTION_BASELINE_TERM_MONTHS);
      if (monthly != null) el.textContent = formatSubscriptionShekels(monthly);
    });
  }

  function loadSubscriptionPlans() {
    var ossApiBase = window.subscriptionSettings && window.subscriptionSettings.ossApiBase;
    if (!ossApiBase || typeof fetch !== 'function') return;
    fetch(ossApiBase + '/api/public/plans')
      .then(function (response) {
        if (!response.ok) throw new Error('plans request failed: ' + response.status);
        return response.json();
      })
      .then(function (data) {
        var plans = (data && data.plans) || [];
        plans.forEach(function (plan) {
          var id = subscriptionNumericId(plan.shopify_variant_id);
          var agorot = Number(plan.monthly_price_agorot);
          if (id && isFinite(agorot) && agorot > 0) subscriptionPlanMonthlyAgorot[id] = agorot;
        });
        renderSubscriptionQuantityCardPrices();
        updateSubscriptionEditQuantityPrices();
        if (currentSubscriptionStepIndex() === 'results') renderSubscriptionResults();
      })
      .catch(function (err) {
        // Keep the Liquid-computed prices already on the page.
        console.warn('Subscription plans unavailable, using theme prices:', err);
      });
  }

  function formatSubscriptionShekels(amount) {
    return Math.round(amount) + ' ₪';
  }

  function computeSubscriptionPrice(answers) {
    var variant = findSubscriptionVariant(answers.quantity, answers.frequency);
    var monthlyExact = subscriptionMonthlyPrice(answers.quantity, answers.frequency);
    if (!variant || monthlyExact == null) return null;
    // The term is pinned to SUBSCRIPTION_BASELINE_TERM_MONTHS, so there is no
    // shorter-commitment baseline to compare against — per-quantity savings are
    // computed in renderSubscriptionResults.
    var months = parseInt(answers.frequency, 10) || SUBSCRIPTION_BASELINE_TERM_MONTHS;
    var monthly = Math.round(monthlyExact);
    return {
      variant: variant,
      total: monthly * months,
      monthly: monthly,
      annual: monthly * 12,
      shipments: months,
      perShipment: monthly,
    };
  }

  /* ── TabNav a11y button: pin beside results title only ─────────────────── */
  var subscriptionA11yBtn = null;
  var subscriptionA11yPinned = false;
  var subscriptionA11yPollId = null;

  function findSubscriptionA11yButton(root) {
    if (subscriptionA11yBtn && subscriptionA11yBtn.isConnected) return subscriptionA11yBtn;
    function walk(node) {
      if (!node) return null;
      if (node.shadowRoot) {
        var btn = node.shadowRoot.querySelector('#tr-button-mobile');
        if (btn) return btn;
        var nested = walk(node.shadowRoot);
        if (nested) return nested;
      }
      var children = node.children || [];
      for (var i = 0; i < children.length; i++) {
        var found = walk(children[i]);
        if (found) return found;
      }
      return null;
    }
    subscriptionA11yBtn = walk(root || document.body);
    return subscriptionA11yBtn;
  }

  function pinSubscriptionA11yToTitle() {
    var slot = document.getElementById('subscriptionResultsA11ySlot');
    var btn = findSubscriptionA11yButton();
    if (!slot || !btn) return false;
    var rect = slot.getBoundingClientRect();
    btn.style.setProperty('position', 'fixed', 'important');
    btn.style.setProperty('top', Math.round(rect.top) + 'px', 'important');
    btn.style.setProperty('left', Math.round(rect.left) + 'px', 'important');
    btn.style.setProperty('right', 'auto', 'important');
    btn.style.setProperty('bottom', 'auto', 'important');
    btn.style.setProperty('transform', 'none', 'important');
    btn.style.setProperty('margin', '0', 'important');
    subscriptionA11yPinned = true;
    return true;
  }

  function unpinSubscriptionA11yButton() {
    var btn = findSubscriptionA11yButton();
    if (btn && subscriptionA11yPinned) {
      btn.style.removeProperty('position');
      btn.style.removeProperty('top');
      btn.style.removeProperty('left');
      btn.style.removeProperty('right');
      btn.style.removeProperty('bottom');
      btn.style.removeProperty('transform');
      btn.style.removeProperty('margin');
    }
    subscriptionA11yPinned = false;
  }

  function stopSubscriptionA11yPinning() {
    if (subscriptionA11yPollId) {
      clearInterval(subscriptionA11yPollId);
      subscriptionA11yPollId = null;
    }
    unpinSubscriptionA11yButton();
  }

  function startSubscriptionA11yPinning() {
    pinSubscriptionA11yToTitle();
    if (subscriptionA11yPollId) return;
    var attempts = 0;
    subscriptionA11yPollId = setInterval(function () {
      pinSubscriptionA11yToTitle();
      attempts += 1;
      if (attempts > 40 && subscriptionA11yPinned) {
        clearInterval(subscriptionA11yPollId);
        subscriptionA11yPollId = setInterval(pinSubscriptionA11yToTitle, 1000);
      }
    }, 250);
  }

  function syncSubscriptionA11yButton(onResults) {
    if (onResults) startSubscriptionA11yPinning();
    else stopSubscriptionA11yPinning();
  }

  /* ── Analytics ─────────────────────────────────────────────────────────── */
  function subscriptionScreenName(stepIndex) {
    if (stepIndex === 'intro') return 'Subscription:Intro';
    if (stepIndex === 'results') return 'Subscription:Final Offer';
    var page = parseInt(stepIndex, 10);
    if (page >= 1 && page <= SUBSCRIPTION_TOTAL_STEPS) return 'Subscription:Page ' + page;
    return null;
  }

  function trackSubscriptionScreenView(stepIndex) {
    var screenName = subscriptionScreenName(stepIndex);
    if (!screenName) return;
    if (typeof analytics !== 'undefined' && analytics.trackScreenView) {
      analytics.trackScreenView(screenName);
    }
  }

  function trackSubscriptionSelection(field, selection) {
    var step = SUBSCRIPTION_FIELD_TO_STEP[field];
    if (!step) return;
    if (typeof analytics !== 'undefined' && analytics.track) {
      analytics.track('Subscription Selection', {
        Step: step,
        Selection: selection,
      });
    }
  }

  function subscriptionCheckoutProperties(answers) {
    return {
      Taste: answers.profile,
      Grind: answers.grind,
      Amount: answers.quantity,
      Term: answers.frequency,
      Date: answers.deliveryDay,
    };
  }

  function trackSubscriptionCheckout(answers) {
    if (typeof analytics !== 'undefined' && analytics.track) {
      analytics.track('Subscription Checkout', {
        Properties: JSON.stringify(subscriptionCheckoutProperties(answers)),
      });
    }
    if (window.trackChatGPTCheckoutStarted) window.trackChatGPTCheckoutStarted();
  }

  /* ── Step machine ──────────────────────────────────────────────────────── */
  function activateSubscriptionStep(stepIndex) {
    document.querySelectorAll('.subscription-step').forEach(function (el) {
      el.classList.remove('active');
    });
    var target = document.querySelector('.subscription-step[data-step-index="' + stepIndex + '"]');
    if (target) target.classList.add('active');
    var wrapper = document.getElementById('subscriptionWrapper');
    if (wrapper) {
      wrapper.classList.toggle('subscription-results-visible', stepIndex === 'results');
    }
    syncSubscriptionA11yButton(stepIndex === 'results');
    trackSubscriptionScreenView(stepIndex);
  }

  function currentSubscriptionStepIndex() {
    var active = document.querySelector('.subscription-step.active[data-step-index]');
    return active ? active.getAttribute('data-step-index') : null;
  }

  function goToPreviousSubscriptionStep() {
    var current = currentSubscriptionStepIndex();
    if (!current || current === '1' || current === 'intro' || current === 'results') return;
    activateSubscriptionStep(String(parseInt(current, 10) - 1));
  }
  window.goToPreviousSubscriptionStep = goToPreviousSubscriptionStep;

  function startSubscriptionFromIntro() {
    activateSubscriptionStep('1');
  }
  window.startSubscriptionFromIntro = startSubscriptionFromIntro;

  function goToNextSubscriptionStep() {
    var current = currentSubscriptionStepIndex();
    if (!current || current === 'results') return;
    var next = parseInt(current, 10) + 1;
    if (next > SUBSCRIPTION_TOTAL_STEPS) {
      showSubscriptionResults();
    } else {
      activateSubscriptionStep(String(next));
    }
  }

  function syncSubscriptionOptionSelectedStates() {
    var answers = window.subscriptionAnswers;
    document.querySelectorAll('.subscription-options[data-field]').forEach(function (group) {
      var field = group.getAttribute('data-field');
      var current = answers[field] == null ? null : String(answers[field]);
      group.querySelectorAll('.subscription-option').forEach(function (btn) {
        btn.classList.toggle('selected', current !== null && btn.getAttribute('data-value') === current);
      });
    });
  }

  function persistSubscriptionState() {
    try {
      sessionStorage.setItem(
        SUBSCRIPTION_RESULTS_STATE_KEY,
        JSON.stringify({
          answers: window.subscriptionAnswers,
          showResults: currentSubscriptionStepIndex() === 'results',
        })
      );
    } catch (e) {
      /* sessionStorage unavailable (e.g. private mode) — flow still works, just without persistence */
    }
  }

  function attachSubscriptionOptionListeners() {
    document.querySelectorAll('.subscription-options[data-field]').forEach(function (group) {
      var field = group.getAttribute('data-field');
      group.querySelectorAll('.subscription-option').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var value = btn.getAttribute('data-value');
          window.subscriptionAnswers[field] = field === 'quantity' ? parseInt(value, 10) : value;
          trackSubscriptionSelection(field, window.subscriptionAnswers[field]);
          group.querySelectorAll('.subscription-option.selected').forEach(function (el) {
            el.classList.remove('selected');
          });
          btn.classList.add('selected');
          persistSubscriptionState();
          setTimeout(goToNextSubscriptionStep, SUBSCRIPTION_AUTO_ADVANCE_MS);
        });
      });
    });
  }

  /* ── Coffee samples carousel ───────────────────────────────────────────── */
  /* Answer profile → custom.coffee_taste metafield values it matches. */
  var SUBSCRIPTION_PROFILE_COFFEE_TASTES = {
    italian: ['Chocolate', 'Sweet-Chocolate'],
    classic: ['Sweet', 'Sweet-Chocolate'],
    modern: ['Fruity', 'Fruity-Sweet'],
  };
  var SUBSCRIPTION_SAMPLES_COUNT = 6;
  var subscriptionSamplesCache = {};

  function normalizeSubscriptionMetaList(val) {
    if (val == null || val === '') return [];
    if (Array.isArray(val)) return val.filter(Boolean).map(String);
    return [String(val)];
  }

  function pickSubscriptionSamples(profile) {
    if (subscriptionSamplesCache[profile]) return subscriptionSamplesCache[profile];
    var tastes = SUBSCRIPTION_PROFILE_COFFEE_TASTES[profile] || [];
    var pool = window.SubscriptionScoring.loadProducts().filter(function (product) {
      if (!product.available || !product.image) return false;
      var productTastes = normalizeSubscriptionMetaList(product.metafields && product.metafields.coffee_taste);
      return productTastes.some(function (taste) {
        return tastes.indexOf(taste.trim()) !== -1;
      });
    });
    // Fill each slot with a random bag whose vendor differs from the previous one,
    // preferring vendors not shown yet. Stops early rather than put two bags
    // from the same vendor side by side.
    var picked = [];
    var usedVendors = {};
    var previousVendor = null;
    while (picked.length < SUBSCRIPTION_SAMPLES_COUNT && pool.length) {
      var candidates = pool.filter(function (product) {
        return subscriptionSampleVendor(product) !== previousVendor;
      });
      var fresh = candidates.filter(function (product) {
        return !usedVendors[subscriptionSampleVendor(product)];
      });
      var choices = fresh.length ? fresh : candidates;
      if (!choices.length) break;
      var choice = choices[Math.floor(Math.random() * choices.length)];
      pool.splice(pool.indexOf(choice), 1);
      previousVendor = subscriptionSampleVendor(choice);
      usedVendors[previousVendor] = true;
      picked.push(choice);
    }
    subscriptionSamplesCache[profile] = picked;
    return picked;
  }

  function subscriptionSampleVendor(product) {
    return product.vendor_short_name || product.vendor || '';
  }

  function escapeSubscriptionHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function subscriptionSampleHtml(product) {
    return (
      '<button type="button" class="subscription-sample-card" data-product-id="' +
      escapeSubscriptionHtml(product.id) +
      '">' +
      '<img class="subscription-sample-img" src="' +
      escapeSubscriptionHtml(product.image) +
      '" alt="' +
      escapeSubscriptionHtml(product.title) +
      '" width="120" height="150" loading="lazy">' +
      '</button>'
    );
  }

  function renderSubscriptionSamples(profile) {
    var section = document.getElementById('subscriptionSamples');
    var track = document.getElementById('subscriptionSamplesTrack');
    if (!section || !track) return;
    var samples = profile ? pickSubscriptionSamples(profile) : [];
    track.innerHTML = samples.map(function (p) { return subscriptionSampleHtml(p); }).join('');
    section.hidden = !samples.length;
  }

  /* ── Coffee sample details modal ───────────────────────────────────────── */
  var subscriptionSampleModalReturnFocus = null;

  function openSubscriptionSampleModal(product) {
    var overlay = document.getElementById('subscriptionSampleModalOverlay');
    if (!overlay || !product) return;
    var img = document.getElementById('subscriptionSampleModalImg');
    if (img) {
      img.src = product.image_large || product.image || '';
      img.alt = product.title || '';
    }
    var vendorEl = document.getElementById('subscriptionSampleModalVendor');
    if (vendorEl) vendorEl.textContent = product.vendor || product.vendor_short_name || '';
    var titleEl = document.getElementById('subscriptionSampleModalTitle');
    if (titleEl) titleEl.textContent = product.title || '';
    var notesEl = document.getElementById('subscriptionSampleModalNotes');
    if (notesEl) {
      var notes = normalizeSubscriptionMetaList(product.metafields && product.metafields.coffee_tastes);
      notesEl.innerHTML = notes
        .map(function (note) {
          return '<span class="subscription-sample-modal-note">' + escapeSubscriptionHtml(note) + '</span>';
        })
        .join('');
      notesEl.hidden = !notes.length;
    }
    var descEl = document.getElementById('subscriptionSampleModalDesc');
    if (descEl) {
      descEl.textContent = product.short_description || '';
      descEl.hidden = !product.short_description;
    }
    subscriptionSampleModalReturnFocus = document.activeElement;
    overlay.classList.add('open');
    var closeBtn = document.getElementById('subscriptionSampleModalClose');
    if (closeBtn) closeBtn.focus();
  }

  function closeSubscriptionSampleModal() {
    var overlay = document.getElementById('subscriptionSampleModalOverlay');
    if (!overlay || !overlay.classList.contains('open')) return;
    overlay.classList.remove('open');
    if (subscriptionSampleModalReturnFocus && subscriptionSampleModalReturnFocus.focus) {
      subscriptionSampleModalReturnFocus.focus();
    }
    subscriptionSampleModalReturnFocus = null;
  }
  window.closeSubscriptionSampleModal = closeSubscriptionSampleModal;

  function attachSubscriptionSampleListeners() {
    var track = document.getElementById('subscriptionSamplesTrack');
    if (track) {
      track.addEventListener('click', function (e) {
        var card = e.target.closest('.subscription-sample-card');
        if (!card) return;
        var id = card.getAttribute('data-product-id');
        var product = window.SubscriptionScoring.loadProducts().filter(function (p) {
          return String(p.id) === id;
        })[0];
        openSubscriptionSampleModal(product);
      });
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeSubscriptionSampleModal();
    });
  }

  /* ── Results rendering ─────────────────────────────────────────────────── */
  function renderSubscriptionResults() {
    var answers = window.subscriptionAnswers;

    var kg = answers.quantity ? answers.quantity / 4 : null;

    var summaryValues = {
      profile: [SUBSCRIPTION_PROFILE_LABELS[answers.profile] || '', SUBSCRIPTION_PROFILE_SHORT_DESC[answers.profile] || ''],
      grind: [SUBSCRIPTION_GRIND_LABELS[answers.grind] || '', ''],
      quantity: [
        answers.quantity ? answers.quantity + ' שקיות · ' + kg + ' ק״ג' : '',
        '',
      ],
      deliveryDay: [SUBSCRIPTION_DELIVERY_DAY_LABELS[answers.deliveryDay] || '', ''],
    };
    Object.keys(summaryValues).forEach(function (field) {
      var valueEl = document.querySelector('[data-summary-value="' + field + '"]');
      var subEl = document.querySelector('[data-summary-sub="' + field + '"]');
      if (valueEl) valueEl.textContent = summaryValues[field][0];
      if (subEl) {
        subEl.textContent = summaryValues[field][1];
        subEl.hidden = !summaryValues[field][1];
      }
    });

    renderSubscriptionSamples(answers.profile);

    var price = computeSubscriptionPrice(answers);
    var planLabelEl = document.getElementById('subscriptionPaymentPlanLabel');
    var monthlyEl = document.getElementById('subscriptionPaymentMonthly');
    var totalEl = document.getElementById('subscriptionPaymentTotal');
    var noteEl = document.getElementById('subscriptionPaymentNote');
    var savingsRow = document.getElementById('subscriptionPaymentSavingsRow');
    var savingsEl = document.getElementById('subscriptionPaymentSavings');

    var savings = 0;
    if (price) {
      if (parseInt(answers.quantity, 10) === 16) {
        // 4kg (16 bags) bundle: 20 ₪ discount per delivery/package. Billing is
        // monthly, so the saving is shown per month.
        savings = 20;
      } else if (kg && kg > 1 && parseInt(answers.quantity, 10) !== 8) {
        // Savings vs the 1kg per-bag rate at the same frequency (matches the
        // "חסכת X ₪" tag shown on the step-3 quantity cards). 2kg gets free shipping only.
        var oneKgMonthly = subscriptionMonthlyPrice(4, answers.frequency);
        if (oneKgMonthly != null) {
          savings = Math.max(0, Math.round(oneKgMonthly * kg - price.monthly));
        }
      }
    }

    if (planLabelEl) planLabelEl.textContent = price ? kg + ' ק״ג לחודש' : '';
    if (monthlyEl) monthlyEl.textContent = price ? formatSubscriptionShekels(price.monthly) + ' לחודש' : '';
    if (totalEl) totalEl.textContent = '';
    if (noteEl) {
      noteEl.textContent = price
        ? 'החיוב מתבצע כל חודש לפני המשלוח. המנוי נמשך עד שתבחרו לבטל, וניתן להשהות או לבטל בכל עת.'
        : '';
    }
    if (savingsRow) savingsRow.hidden = !savings;
    if (savingsEl) savingsEl.textContent = savings ? formatSubscriptionShekels(savings) + " לחודש" : '';

    persistSubscriptionState();
  }

  function showSubscriptionResults() {
    activateSubscriptionStep('results');
    renderSubscriptionResults();
  }

  /* ── Close / restart ───────────────────────────────────────────────────── */
  function closeSubscriptionForm() {
    var redirect = (window.subscriptionSettings && window.subscriptionSettings.closeRedirect) || '/';
    window.location.assign(redirect);
  }
  window.closeSubscriptionForm = closeSubscriptionForm;

  function clearSubscriptionResultsState() {
    try {
      sessionStorage.removeItem(SUBSCRIPTION_RESULTS_STATE_KEY);
    } catch (e) {
      /* no-op */
    }
  }
  window.clearSubscriptionResultsState = clearSubscriptionResultsState;

  function restartSubscriptionFlow() {
    clearSubscriptionResultsState();
    window.subscriptionAnswers = { quantity: null, frequency: SUBSCRIPTION_BASELINE_TERM_MONTHS, deliveryDay: null, profile: null, grind: null };
    document.querySelectorAll('.subscription-option.selected').forEach(function (el) {
      el.classList.remove('selected');
    });
    activateSubscriptionStep('intro');
  }
  window.restartSubscriptionFlow = restartSubscriptionFlow;

  /* ── Edit preferences bottom sheet ─────────────────────────────────────── */
  /*
   * Refresh the quantity <option> labels so each one shows its per-shipment
   * price given the currently selected frequency. Prices come from
   * subscriptionMonthlyPrice (OSS plans, falling back to the variant price) —
   * the same source used by computeSubscriptionPrice.
   */
  function updateSubscriptionEditQuantityPrices() {
    var quantitySelect = document.getElementById('subscriptionEditQuantity');
    if (!quantitySelect) return;
    var frequency = window.subscriptionAnswers.frequency || SUBSCRIPTION_BASELINE_TERM_MONTHS;
    for (var i = 0; i < quantitySelect.options.length; i++) {
      var opt = quantitySelect.options[i];
      if (!opt.dataset.baseLabel) opt.dataset.baseLabel = opt.textContent;
      var qty = parseInt(opt.value, 10);
      var monthly = subscriptionMonthlyPrice(qty, frequency);
      if (monthly != null) {
        opt.textContent = opt.dataset.baseLabel + ' — ' + formatSubscriptionShekels(monthly);
      } else {
        opt.textContent = opt.dataset.baseLabel;
      }
    }
    updateSubscriptionSheetRowValue(quantitySelect);
  }

  function populateSubscriptionEditSheet() {
    var answers = window.subscriptionAnswers;
    Object.keys(SUBSCRIPTION_FIELD_TO_SHEET_ID).forEach(function (field) {
      var el = document.getElementById(SUBSCRIPTION_FIELD_TO_SHEET_ID[field]);
      if (el && answers[field] != null) {
        el.value = String(answers[field]);
      }
      if (el) updateSubscriptionSheetRowValue(el);
    });
    updateSubscriptionEditQuantityPrices();
  }

  function openSubscriptionEditSheet() {
    populateSubscriptionEditSheet();
    var overlay = document.getElementById('subscriptionEditSheetOverlay');
    if (overlay) overlay.classList.add('open');
  }
  window.openSubscriptionEditSheet = openSubscriptionEditSheet;

  function closeSubscriptionEditSheet() {
    var overlay = document.getElementById('subscriptionEditSheetOverlay');
    if (overlay) overlay.classList.remove('open');
  }
  window.closeSubscriptionEditSheet = closeSubscriptionEditSheet;

  function applySubscriptionEditSheet() {
    Object.keys(SUBSCRIPTION_FIELD_TO_SHEET_ID).forEach(function (field) {
      var el = document.getElementById(SUBSCRIPTION_FIELD_TO_SHEET_ID[field]);
      if (!el) return;
      var nextValue = field === 'quantity' ? parseInt(el.value, 10) : el.value;
      var previous = window.subscriptionAnswers[field];
      window.subscriptionAnswers[field] = nextValue;
      if (String(previous) !== String(nextValue)) {
        trackSubscriptionSelection(field, nextValue);
      }
    });
    syncSubscriptionOptionSelectedStates();
    renderSubscriptionResults();
    closeSubscriptionEditSheet();
  }
  window.applySubscriptionEditSheet = applySubscriptionEditSheet;

  /* ── Checkout: create a Shopify draft order via the backend ────────────── */
  /*
   * Contact, shipping and the month-1 payment are all collected by Shopify
   * checkout (PayPlus Shopify app). OPS prices the plan from OSS, so no price
   * is sent from here.
   */
  function submitSubscriptionToCart() {
    var answers = window.subscriptionAnswers;
    if (!answers.quantity || !answers.frequency || !answers.deliveryDay || !answers.profile || !answers.grind) return;

    var btn = document.getElementById('subscriptionCtaBtn');
    var label = document.getElementById('subscriptionCtaLabel');
    var originalLabel = label ? label.textContent : '';
    var variant = findSubscriptionVariant(answers.quantity, answers.frequency);
    var variantId = variant && variant.id;

    if (!variantId) {
      console.error('Subscription checkout: no matching quantity/frequency variant on the subscription product.');
      if (label) label.textContent = 'שגיאה, נסו שוב';
      return;
    }

    // OSS creates the Shopify checkout (a draft order at the monthly price).
    var apiBase = (window.subscriptionSettings && window.subscriptionSettings.ossApiBase) || '';
    if (!apiBase) {
      console.error('Subscription checkout: window.subscriptionSettings.ossApiBase is not configured.');
      if (label) label.textContent = 'שגיאה, נסו שוב';
      return;
    }

    if (btn) btn.disabled = true;
    if (label) label.textContent = 'מעביר לתשלום...';
    trackSubscriptionCheckout(answers);

    fetch(apiBase + '/api/public/checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        variant_id: String(variantId),
        term_months: parseInt(answers.frequency, 10) || SUBSCRIPTION_BASELINE_TERM_MONTHS,
        bags: parseInt(answers.quantity, 10),
        delivery_day: parseInt(answers.deliveryDay, 10),
        profile: answers.profile,
        grind: answers.grind,
      }),
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (err) {
            throw err;
          });
        }
        return response.json();
      })
      .then(function (data) {
        if (!data || !data.checkout_url) throw new Error('missing checkout_url');
        // A draft-order invoice opens in the shop's primary language (English) unless
        // told otherwise, so carry the storefront locale over.
        var checkoutUrl = new URL(data.checkout_url);
        checkoutUrl.searchParams.set('locale', (window.Shopify && window.Shopify.locale) || 'he');
        window.location.assign(checkoutUrl.toString());
      })
      .catch(function (err) {
        console.error('Subscription checkout failed:', err);
        if (btn) btn.disabled = false;
        if (label) label.textContent = originalLabel || 'שגיאה, נסו שוב';
      });
  }
  window.submitSubscriptionToCart = submitSubscriptionToCart;

  // Left over from the removed on-site contact step — drop any stale values.
  try {
    localStorage.removeItem('originSubscriptionContact');
    sessionStorage.removeItem('subscriptionPaymentPending');
    sessionStorage.removeItem('originSubscriptionEmail');
  } catch (e) {}

  /* ── Init ──────────────────────────────────────────────────────────────── */
  function restoreSubscriptionState() {
    try {
      var raw = sessionStorage.getItem(SUBSCRIPTION_RESULTS_STATE_KEY);
      if (!raw) return;
      var parsed = JSON.parse(raw);
      if (parsed && parsed.answers) {
        window.subscriptionAnswers = Object.assign(
          { quantity: null, frequency: SUBSCRIPTION_BASELINE_TERM_MONTHS, deliveryDay: null, profile: null, grind: null },
          parsed.answers
        );
        // Term is no longer customer-selectable — always pin to the baseline,
        // regardless of any value an older session may have stored.
        window.subscriptionAnswers.frequency = SUBSCRIPTION_BASELINE_TERM_MONTHS;
        syncSubscriptionOptionSelectedStates();
        if (parsed.showResults) {
          showSubscriptionResults();
        } else {
          // Skip the intro if the user already started answering in this session.
          var a = window.subscriptionAnswers;
          var hasProgress = a.quantity || a.deliveryDay || a.profile || a.grind;
          if (hasProgress) activateSubscriptionStep('1');
        }
      }
    } catch (e) {
      /* ignore malformed/unavailable storage */
    }
  }

  function updateSubscriptionSheetRowValue(select) {
    if (!select) return;
    var row = select.closest('.subscription-sheet-row');
    if (!row) return;
    var valueEl = row.querySelector('.subscription-sheet-value');
    if (!valueEl) return;
    var opt = select.options[select.selectedIndex];
    valueEl.textContent = opt ? opt.textContent : '';
  }

  function updateAllSubscriptionSheetRowValues() {
    document.querySelectorAll('.subscription-sheet-select').forEach(updateSubscriptionSheetRowValue);
  }

  /*
   * The native <select> is absolutely-positioned across the entire row with
   * opacity 0 (see subscription.css). That makes the whole row a real hit
   * target for the OS-native picker — necessary for Safari, where
   * HTMLSelectElement.showPicker() is unavailable and a synthetic .click()
   * on a <select> does not open the picker. We only need to keep the visible
   * value display in sync with the current selection.
   */
  function attachSubscriptionSheetRowListeners() {
    var selects = document.querySelectorAll('.subscription-sheet-select');
    selects.forEach(function (select) {
      select.addEventListener('change', function () {
        updateSubscriptionSheetRowValue(select);
        // Keep the priced quantity labels in sync with the current selection.
        if (select.id === 'subscriptionEditQuantity') {
          updateSubscriptionEditQuantityPrices();
        }
      });
    });
    updateAllSubscriptionSheetRowValues();
  }

  document.addEventListener('DOMContentLoaded', function () {
    attachSubscriptionOptionListeners();
    attachSubscriptionSheetRowListeners();
    attachSubscriptionSampleListeners();
    restoreSubscriptionState();
    loadSubscriptionPlans();
    if (currentSubscriptionStepIndex() === 'intro') {
      trackSubscriptionScreenView('intro');
    }
    // Mobile scrolls .subscription-results-scroll; desktop scrolls the wrapper.
    ['.subscription-results-scroll', '#subscriptionWrapper'].forEach(function (selector) {
      var scroller = document.querySelector(selector);
      if (!scroller) return;
      scroller.addEventListener('scroll', function () {
        if (subscriptionA11yPinned) pinSubscriptionA11yToTitle();
      });
    });
    window.addEventListener('resize', function () {
      if (subscriptionA11yPinned) pinSubscriptionA11yToTitle();
    });
  });
})();
